import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import decode from 'jwt-decode';
import { useRecoilState } from 'recoil';
import { SECOND } from 'time-constants';
import { number, object, string } from 'yup';

import { apiConfig } from '@constants';
import { userSettingsState } from '@store';

import { useGetAuth0DeviceActivation } from './useGetAuth0DeviceActivation';

const refetchIntervalFallback = 900 * SECOND;
const retryDelayFallback = 5 * SECOND;

const tokenSchema = object({
  access_token: string().required(),
  id_token: string().required(),
  token_type: string().required(),
  expires_in: number().required(),
  scope: string().required(),
});

const getAuth0AccessToken = async device_code => {
  // You MUST use append here for support on tizen!
  const payload = new URLSearchParams();

  payload.append('client_id', apiConfig.AUTH0_CLIENT_ID);
  payload.append('device_code', device_code);
  payload.append('scope', 'openid profile email');
  payload.append('grant_type', 'urn:ietf:params:oauth:grant-type:device_code');

  const { data } = await axios.post(apiConfig.AUTH0_TOKEN_URL, payload);

  return tokenSchema.validate(data);
};

const getDSPCustomerToken = (jwtToken, key) => decode(jwtToken)[key];

export const useGetAuth0AccessToken = () => {
  const [{ dspCustomerToken }, setUserSettings] = useRecoilState(userSettingsState);
  const { data: deviceActivationData } = useGetAuth0DeviceActivation(!dspCustomerToken);
  const [refetchInterval, setRefetchInterval] = useState(refetchIntervalFallback);

  const {
    device_code: deviceCode,
    expirationTime,
    interval,
    user_code: userCode,
    verification_uri: registerDeviceUrl,
  } = deviceActivationData ?? {};
  const { data } = useQuery(['getAuth0AccessToken', deviceCode], () => getAuth0AccessToken(deviceCode), {
    enabled: Boolean(deviceCode && !dspCustomerToken),
    retry: true,
    retryDelay: (interval ?? retryDelayFallback) * SECOND,
    refetchInterval,
    refetchOnWindowFocus: false,
    onSuccess: ({ expires_in }) => setRefetchInterval(expires_in * SECOND),
  });

  useEffect(() => {
    if (data) {
      setUserSettings(currentState => ({
        ...currentState,
        dspCustomerToken: getDSPCustomerToken(data.access_token, apiConfig.DSP_CUSTOMER_TOKEN_KEY),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return {
    deviceCode,
    expirationTime,
    userCode,
    registerDeviceUrl,
  };
};
